import { useQueryClient } from '@tanstack/react-query';
import { useDeleteTaxZone } from './useDeleteTaxZone';
import { useDeleteServiceArea } from '@/hooks/service-areas/useDeleteServiceArea';
import { fetchServiceAreas } from '@/hooks/service-areas/useServiceAreas';
import { serviceAreaKeys } from '@/lib/queryKeys';
import type { ServiceArea } from '@/types/service-area';

function normalizeZipCode(zipCode: string) {
  return zipCode.trim().toUpperCase().replace(/\s/g, '');
}

interface DeleteCallbacks {
  onSuccess?: () => void;
  onError?: () => void;
  /**
   * Called when the tax zone was deleted but the linked service area cleanup
   * failed. The zone is already gone so the UI should still close, but callers
   * should surface a warning rather than a success signal.
   * Falls back to onSuccess if not provided (closes UI silently).
   */
  onPartialSuccess?: () => void;
}

/**
 * Deletes a tax zone and its associated service area (if any) as a single
 * combined action. All delete paths in the tax views use this hook so
 * behavior stays consistent regardless of which view triggers the delete.
 *
 * The service area lookup is resolved at mutation time via queryClient so it
 * is never stale: returns cached data if the query is fresh, otherwise fetches
 * on demand. This avoids the race condition where useServiceAreas() hasn't
 * loaded yet and the cleanup is silently skipped.
 */
export function useDeleteTaxZoneWithArea() {
  const queryClient = useQueryClient();
  const deleteZone = useDeleteTaxZone();
  const deleteArea = useDeleteServiceArea();

  function mutate(
    { id, zipCode }: { id: string; zipCode: string },
    { onSuccess, onError, onPartialSuccess }: DeleteCallbacks = {},
  ) {
    deleteZone.mutate(
      { id },
      {
        onSuccess: () => {
          // Resolve service areas at mutation time — fetchQuery uses the cache
          // if data is present, otherwise fires a real request. This guarantees
          // we never skip cleanup because the query hadn't loaded yet.
          queryClient
            .fetchQuery<ServiceArea[]>({
              queryKey: serviceAreaKeys.list(),
              queryFn: fetchServiceAreas,
            })
            .then((serviceAreas) => {
              const sa = serviceAreas.find(
                (a) => normalizeZipCode(a.zipCode) === normalizeZipCode(zipCode),
              );
              if (sa?.id) {
                deleteArea.mutate(
                  { id: sa.id },
                  {
                    onSuccess,
                    onError: onPartialSuccess ?? onSuccess,
                  },
                );
              } else {
                onSuccess?.();
              }
            })
            .catch(() => {
              // Could not determine if a service area exists — treat as partial
              // failure so callers can warn the user to verify manually.
              (onPartialSuccess ?? onSuccess)?.();
            });
        },
        onError,
      },
    );
  }

  return {
    mutate,
    isPending: deleteZone.isPending || deleteArea.isPending,
  };
}
